import { useState } from 'react'
import { Users, Plus, Trash2, Copy, Search, Pencil, Save } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import { definitionService, teamService } from '../services/api'
import { CircleRoleSummary } from '../components/CircleRoleSummary'
import { DefinitionTransfer } from '../components/DefinitionTransfer'
import { TeamModelEditor } from '../components/TeamModelEditor'
import { TeamResourceBoard } from '../components/TeamResourceBoard'
import type { TeamTemplate } from '../types/team'
import { formatTeamCircles } from '../lib/formatTeamCircles'

function CircleRoleLinks({ team }: { team: TeamTemplate }) {
  const circles = team.circles ?? []
  if (circles.length === 0) {
    return <p className="text-sm text-primary whitespace-pre-wrap max-w-xl">{circlesAndRoles(team)}</p>
  }
  return <CircleRoleSummary teamId={team.id} circles={circles} />
}

async function exportError(error: unknown): Promise<string> {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: unknown } }).response?.data
    if (data instanceof Blob) {
      try {
        const payload = JSON.parse(await data.text()) as { error?: { message?: string } }
        if (payload.error?.message) {
          return payload.error.message
        }
      } catch {
        return 'L\'export de l\'équipe a échoué'
      }
    }
  }
  return 'L\'export de l\'équipe a échoué'
}

function circlesAndRoles(team: TeamTemplate): string {
  const circles = team.circles ?? []
  if (circles.length > 0) {
    return formatTeamCircles(circles)
  }
  if (team.roles.length > 0) {
    return team.roles.map((role) => role.name).join(', ')
  }
  return '—'
}

export function Teams() {
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [editedTeam, setEditedTeam] = useState<TeamTemplate | null>(null)
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [showCreateTeamForm, setShowCreateTeamForm] = useState(false)
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    circleKind: 'agent' as 'human' | 'agent'
  })

  const { data: teams, isLoading, error } = useQuery(
    'teamTemplates',
    teamService.getAllTeamTemplates
  )

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le modèle d'équipe "${teamName}" ?\n\nCette action est irréversible.`)) {
      try {
        await teamService.deleteTeamTemplate(teamId)
        queryClient.invalidateQueries('teamTemplates')
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        alert('Erreur lors de la suppression du modèle d\'équipe')
      }
    }
  }

  const handleExportTeam = async (team: TeamTemplate) => {
    setExportingId(team.id)
    try {
      const blob = await definitionService.exportTeamPackage(team.id)
      const url = URL.createObjectURL(blob)
      const link = window.document.createElement('a')
      link.href = url
      link.download = `equipe-${team.name.replace(/[^\w.-]+/g, '-')}.zip`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      alert(await exportError(error))
    } finally {
      setExportingId(null)
    }
  }

  const handleDuplicateTeam = async (teamId: string) => {
    try {
      await teamService.duplicateTeamTemplate(teamId)
      queryClient.invalidateQueries('teamTemplates')
    } catch (error) {
      console.error('Erreur lors de la duplication:', error)
      alert('Erreur lors de la duplication du modèle d\'équipe')
    }
  }

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      alert('Veuillez remplir le nom du modèle d\'équipe')
      return
    }

    try {
      await teamService.createTeamTemplate(newTeam)
      queryClient.invalidateQueries('teamTemplates')
      setShowCreateTeamForm(false)
      setNewTeam({ name: '', description: '', circleKind: 'agent' })
    } catch (error) {
      console.error('Erreur lors de la création:', error)
      alert('Erreur lors de la création du modèle d\'équipe')
    }
  }

  const filteredTeams = teams?.filter(team => 
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 spinner"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur lors du chargement des modèles d'équipes</p>
      </div>
    )
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
        <div className="space-y-6">
          {/* En-tête de page */}
          <div className="page-toolbar flex items-center justify-between">
            <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Modèles d'équipes</h1>
            <div className="flex flex-wrap items-center gap-2">
              <DefinitionTransfer scope="teams" />
              <button
                type="button"
                onClick={() => {
                  setShowCreateTeamForm(true)
                  setNewTeam({ name: '', description: '', circleKind: 'agent' })
                }}
                className="button button--primary"
              >
                <Plus className="w-5 h-5" />
                <span>Nouveau modèle d'équipe</span>
              </button>
            </div>
          </div>

          {/* Formulaire de création de modèle d'équipe */}
          {showCreateTeamForm && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-primary">Créer un modèle d'équipe</h2>
                <button
                  onClick={() => {
                    setShowCreateTeamForm(false)
                    setNewTeam({ name: '', description: '', circleKind: 'agent' })
                  }}
                  className="button button--secondary"
                >
                  Annuler
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nom du modèle *</label>
                  <input id="team-template-name" name="team-template-name"
                    type="text"
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    className="form-input w-full"
                    placeholder="ex: Équipe de développement"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea id="team-template-description" name="team-template-description"
                    value={newTeam.description}
                    onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                    className="form-input w-full"
                    rows={3}
                    placeholder="Description du modèle d'équipe..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="team-template-circle">Premier cercle</label>
                  <select
                    id="team-template-circle"
                    name="team-template-circle"
                    value={newTeam.circleKind}
                    onChange={(e) => setNewTeam({ ...newTeam, circleKind: e.target.value === 'human' ? 'human' : 'agent' })}
                    className="form-input w-full"
                  >
                    <option value="agent">Agents</option>
                    <option value="human">Humains</option>
                  </select>
                  <p className="text-xs text-secondary mt-1">L'équipe démarre avec ce cercle. D'autres cercles s'ajoutent ensuite, et les rôles s'y définissent.</p>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowCreateTeamForm(false)
                      setNewTeam({ name: '', description: '', circleKind: 'agent' })
                    }}
                    className="button button--secondary"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateTeam}
                    className="button button--primary"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Barre de recherche */}
          <div className="card">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 icon-tertiary" />
              <input id="team-template-search" name="team-template-search"
                type="text"
                placeholder="Rechercher un modèle d'équipe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input w-full pl-10"
              />
            </div>
          </div>

          {/* Table des modèles d'équipes */}
          <div className="card overflow-hidden">
            {filteredTeams.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-4 icon-light" />
                <h3 className="text-lg font-medium mb-2 text-primary">Aucun modèle d'équipe trouvé</h3>
                <p className="mb-4 text-secondary">
                  {searchTerm 
                    ? 'Aucun modèle ne correspond à votre recherche.' 
                    : 'Commencez par créer votre premier modèle d\'équipe.'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => {
                      setShowCreateTeamForm(true)
                      setNewTeam({ name: '', description: '', circleKind: 'agent' })
                    }}
                    className="button button--primary"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Créer un modèle d'équipe</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Nom
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Cercles et rôles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Date de création
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider table-header-cell">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeams.map((team) => (
                      <tr
                        key={team.id}
                        className="table-row table-hover-row"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="w-5 h-5 mr-3 text-robot-blue" />
                            <div>
                              <div className="text-sm font-medium text-primary">
                                {team.name}
                              </div>
                              {team.description && (
                                <div className="text-sm text-secondary">
                                  {team.description.length > 50 
                                    ? `${team.description.substring(0, 50)}...` 
                                    : team.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <CircleRoleLinks team={team} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-primary">
                            {formatDate(team.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              type="button"
                              className="action-button action-button--primary"
                              title="Exporter l'équipe"
                              disabled={exportingId === team.id}
                              onClick={() => { void handleExportTeam(team) }}
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditedTeam(editedTeam?.id === team.id ? null : team)}
                              className="action-button action-button--primary"
                              title="Modifier l'équipe"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDuplicateTeam(team.id)
                              }}
                              className="action-button action-button--primary"
                              title="Dupliquer"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteTeam(team.id, team.name)
                              }}
                              className="action-button action-button--danger"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {editedTeam ? (
            <div className="space-y-4">
              <TeamModelEditor key={editedTeam.id} team={editedTeam} onClose={() => setEditedTeam(null)} />
              <TeamResourceBoard mode="template" ownerId={editedTeam.id} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
